import * as React from "react";
import { Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useCpFormValue } from "../../gvc/testNewForm/utils";
import { BasePathContext } from "../../../reactContexts/basePathContext";
import { FormLabel } from "../../../components/antd/form/FormLabel";
import { request } from "../../../services/cpln";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { CloudAccountAzureConfig } from "../../../schema/types/cloudaccount/entity";

export const AzureSummary: React.FC = () => {
  const navigate = useNavigate();
  const basePath = React.useContext(BasePathContext);
  const isEnabled = useCpFormValue("azure.isEnabled");
  const cloudAccount = useCpFormValue("azure.cloudAccount");
  const roleAssignments = useCpFormValue("azure.roleAssignments");

  const azureLogoPath = `/resources/logos/azureLogo.png`;

  const [cloudAccountData, setCloudAccountData] = React.useState<CloudAccountAzureConfig | null>(null);
  const [isLoadingCA, setIsLoadingCA] = React.useState(false);

  React.useEffect(() => {
    if (isEnabled && cloudAccount.link) {
      fetchCloudAccount();
    } else {
      setCloudAccountData(null);
    }
  }, [isEnabled, cloudAccount.link]);

  async function fetchCloudAccount() {
    try {
      setIsLoadingCA(true);
      const res = await request({ url: cloudAccount.link });
      setCloudAccountData(res.data?.data as CloudAccountAzureConfig);
    } catch {
      setCloudAccountData(null);
    } finally {
      setIsLoadingCA(false);
    }
  }

  function handleClickLogo() {
    navigate(`${basePath}/cloud-access-azure`);
  }

  if (!isEnabled) {
    return (
      <button
        onClick={handleClickLogo}
        style={{
          width: "100%",
          height: 100,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--identity-azure-logo-drop)",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--border-radius)",
          cursor: "pointer",
          transition: "box-shadow var(--transition)",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)")}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
      >
        <img src={azureLogoPath} alt="Azure" style={{ height: 30 }} />
      </button>
    );
  }

  const caName = cloudAccount.value || parseLink(cloudAccount.link, { useInputCtx: true }).name;

  return (
    <div
      style={{
        border: "1px solid var(--color-border)",
        borderRadius: "var(--border-radius)",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "16px 20px",
          background: "var(--identity-azure-logo-drop)",
          borderBottom: "4px solid var(--identity-azure-logo-accent)",
        }}
      >
        <img src={azureLogoPath} alt="Azure" style={{ height: 30 }} />
      </div>

      <div style={{ padding: "20px" }}>
        {isLoadingCA ? (
          <div className="flex justify-center py-4">
            <Spin />
          </div>
        ) : (
          <>
            <div className="flex mb-4">
              <div style={{ width: "33.33%" }}>
                <FormLabel>Cloud Account</FormLabel>
                {cloudAccount.link ? (
                  <a
                    href={`/console${cloudAccount.link}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/console${cloudAccount.link}`);
                    }}
                    className="color-link"
                  >
                    {caName}
                  </a>
                ) : (
                  <div>{caName}</div>
                )}
              </div>
              <div style={{ width: "66.67%" }}>
                <FormLabel>Secret Link</FormLabel>
                <div style={{ fontFamily: "var(--font-mono, monospace)" }}>
                  {cloudAccountData?.secretLink || "-"}
                </div>
              </div>
            </div>

            {roleAssignments && roleAssignments.length > 0 ? (
              <div className="mb-4">
                <FormLabel>Role Assignments</FormLabel>
                <div className="flex flex-col gap-2 mt-2">
                  {roleAssignments.map((ra: any) => (
                    <div
                      key={ra.id}
                      style={{
                        border: "1px solid var(--color-border)",
                        borderTop: "3px solid var(--identity-azure-logo-accent)",
                        borderRadius: "var(--border-radius)",
                        padding: "12px",
                      }}
                    >
                      <div className="mb-2">
                        <FormLabel>Scope</FormLabel>
                        <div style={{ fontFamily: "var(--font-mono, monospace)" }}>{ra.scope}</div>
                      </div>
                      <div>
                        <FormLabel>Roles</FormLabel>
                        <ul style={{ margin: 0, paddingLeft: 20, listStyleType: "disc" }}>
                          {(ra.roles || []).map((role: string, i: number) => (
                            <li key={i} style={{ fontFamily: "var(--font-mono, monospace)" }}>
                              {role}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}

        <div className="flex justify-end mt-4">
          <a
            onClick={(e) => {
              e.preventDefault();
              navigate(`${basePath}/cloud-access-azure`);
            }}
            className="color-link"
            style={{ cursor: "pointer", fontWeight: 500 }}
          >
            Details →
          </a>
        </div>
      </div>
    </div>
  );
};
